import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet, bulkDeleteChats, bulkAssignChats, getErrorMessage } from '../../utils/api'
import { Badge } from '../shared/Badge'
import { safeInitial, safeString } from '../../utils/safeString'
import { countryToFlag } from '../../utils/countryFlag'
import { useBulkSelect } from '../../hooks/useBulkSelect'
import { BulkActions, BulkIcons, SelectCheckbox } from '../shared/BulkActionBar'
import { AssignAgentModal } from '../shared/AssignAgentModal'
import { toast } from '../shared/NotificationToast'

interface HistoryChat {
  id: string
  customer_id: string
  customer_name?: string
  visitor_name?: string
  company_name?: string
  company_logo_url?: string
  company_country?: string
  agent_id?: string
  agent_name?: string
  status: string
  tags: string[]
  resolution_summary?: string
  created_at: string
  closed_at?: string
}

const RESOLVED_TAGS = new Set(['resolved', 'closed', 'solved'])
function resolutionOf(c: HistoryChat): string {
  const t = Array.isArray(c.tags) ? c.tags : []
  if (t.length && RESOLVED_TAGS.has(String(t[0]).toLowerCase())) return 'Resolved'
  if (c.status === 'closed') return 'Unresolved'
  return '—'
}
function fmtDateTime(s?: string): string {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
function fmtDuration(a?: string, b?: string): string {
  if (!a || !b) return '—'
  const secs = Math.abs(Math.floor((new Date(b).getTime() - new Date(a).getTime()) / 1000))
  if (!secs) return '—'
  if (secs < 60) return `${secs}s`
  if (secs < 3600) return `${Math.floor(secs / 60)}m`
  const h = Math.floor(secs / 3600); const m = Math.floor((secs % 3600) / 60)
  return m ? `${h}h ${m}m` : `${h}h`
}

export const ChatHistoryView: React.FC = () => {
  const navigate = useNavigate()
  const [chats, setChats] = useState<HistoryChat[]>([])
  const [loading, setLoading] = useState(true)
  // "All" needs to include CLOSED chats. The /queue endpoint's no-filter path
  // returns only the live queue (open/queued/missed), which hid closed
  // conversations from history. The API's queueQuery validator only accepts a
  // SINGLE status from its enum (a comma-joined "open,queued,closed,missed"
  // string is rejected with a 400 — which is why the list came back empty), so
  // for the "All" view we fetch each status in parallel and merge rather than
  // sending one invalid multi-status param. The sentinel below is internal to
  // this component and never sent to the API verbatim.
  const ALL_STATUSES = 'all'
  // The concrete statuses the "All" tab aggregates — each is a valid enum value
  // the /queue endpoint accepts on its own.
  const ALL_STATUS_SET = ['open', 'queued', 'closed', 'missed']
  const [filterStatus, setFilterStatus] = useState(ALL_STATUSES)
  const [filterAgent, _setFilterAgent] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const sel = useBulkSelect()
  const [bulkBusy, setBulkBusy] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)

  useEffect(() => {
    loadHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterAgent, dateFrom, dateTo])

  const loadHistory = async () => {
    setLoading(true)
    try {
      const baseParams: Record<string, string> = {}
      if (filterAgent) baseParams.agent_id = filterAgent
      if (dateFrom) baseParams.from = dateFrom
      if (dateTo) baseParams.to = dateTo

      const fetchStatus = async (status: string): Promise<HistoryChat[]> => {
        const result = await apiGet<{ queue: HistoryChat[] }>('/queue', { ...baseParams, status })
        return result.queue || []
      }

      let merged: HistoryChat[]
      if (filterStatus === ALL_STATUSES) {
        // Fetch each status the "All" view aggregates in parallel, then merge +
        // de-duplicate so a single failing status can't blank the whole list.
        const results = await Promise.all(
          ALL_STATUS_SET.map(s => fetchStatus(s).catch(() => [] as HistoryChat[]))
        )
        const seen = new Set<string>()
        merged = []
        for (const list of results) {
          for (const chat of list) {
            const key = String(chat.id)
            if (seen.has(key)) continue
            seen.add(key)
            merged.push(chat)
          }
        }
        merged.sort((a, b) => {
          const ta = new Date(a.closed_at || a.created_at).getTime()
          const tb = new Date(b.closed_at || b.created_at).getTime()
          return tb - ta
        })
      } else {
        merged = await fetchStatus(filterStatus)
      }
      setChats(merged)
    } catch (err) {
      console.error('Failed to load history:', err)
      setChats([])
    } finally {
      setLoading(false)
    }
  }

  const filteredChats = chats.filter(chat => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return (
        String(chat.id).toLowerCase().includes(q) ||
        chat.customer_name?.toLowerCase().includes(q) ||
        chat.visitor_name?.toLowerCase().includes(q) ||
        chat.company_name?.toLowerCase().includes(q) ||
        chat.agent_name?.toLowerCase().includes(q) ||
        chat.resolution_summary?.toLowerCase().includes(q) ||
        chat.tags?.some(t => t.toLowerCase().includes(q))
      )
    }
    return true
  })

  const visibleIds = filteredChats.map((c) => c.id)

  const handleBulkDelete = async () => {
    if (sel.count === 0) return
    if (!window.confirm(`Permanently delete ${sel.count} chat${sel.count === 1 ? '' : 's'} and all their messages? This cannot be undone.`)) return
    setBulkBusy(true)
    try {
      const res = await bulkDeleteChats(sel.selectedIds)
      toast.success(`Deleted ${res.deleted ?? sel.count} chat${(res.deleted ?? sel.count) === 1 ? '' : 's'}`)
      sel.clear()
      await loadHistory()
    } catch (err) {
      toast.error('Delete failed', getErrorMessage(err, 'Could not delete the selected chats'))
    } finally {
      setBulkBusy(false)
    }
  }

  const handleBulkAssign = async (agentId: string | null) => {
    if (!agentId || sel.count === 0) return
    setBulkBusy(true)
    try {
      const res = await bulkAssignChats(sel.selectedIds, agentId)
      toast.success(`Assigned ${res.assigned ?? sel.count} chat${(res.assigned ?? sel.count) === 1 ? '' : 's'}`)
      setAssignOpen(false)
      sel.clear()
      await loadHistory()
    } catch (err) {
      toast.error('Assign failed', getErrorMessage(err, 'Could not assign the selected chats'))
    } finally {
      setBulkBusy(false)
    }
  }

  const STATUSES = [
    { value: ALL_STATUSES, label: 'All' },
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed' },
    { value: 'queued', label: 'Queued' },
  ]

  return (
    <div className="h-full flex flex-col relative">
      <div className="px-6 pt-6 pb-4 bg-surface border-b border-border">
        <h1 className="text-xl font-bold text-txt-primary mb-1">Chat History</h1>
        <p className="text-sm text-txt-secondary mb-4">Browse and search previous conversations</p>

        {/* Search */}
        <div className="relative mb-4">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary placeholder-txt-tertiary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 transition-colors"
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 border-b border-border">
            {STATUSES.map(s => (
              <button
                key={s.value}
                onClick={() => setFilterStatus(s.value)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  filterStatus === s.value
                    ? 'border-brand-600 text-brand-600'
                    : 'border-transparent text-txt-secondary hover:text-txt-primary'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-1.5 rounded-lg bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
            placeholder="From"
          />
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="px-3 py-1.5 rounded-lg bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
            placeholder="To"
          />
        </div>
      </div>

      {/* Select-all + bulk actions toolbar */}
      {!loading && filteredChats.length > 0 && (
        <div className="flex items-center gap-3 px-6 py-2 bg-surface-secondary/50 border-b border-border">
          <SelectCheckbox
            checked={sel.allSelected(visibleIds)}
            indeterminate={sel.count > 0}
            onChange={() => sel.toggleAll(visibleIds)}
            title="Select all"
          />
          <span className="text-xs text-txt-tertiary">
            {sel.count > 0 ? `${sel.count} selected` : `Select all ${filteredChats.length}`}
          </span>
          {sel.count > 0 && (
            <div className="ml-auto">
              <BulkActions
                busy={bulkBusy}
                actions={[
                  { label: 'Assign', icon: BulkIcons.assign, onClick: () => setAssignOpen(true) },
                  { label: 'Delete', icon: BulkIcons.delete, onClick: handleBulkDelete, variant: 'danger' },
                ]}
              />
            </div>
          )}
        </div>
      )}

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <span className="text-sm text-txt-secondary">Loading history...</span>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48">
            <p className="text-sm text-txt-secondary">No conversations found</p>
          </div>
        ) : (
          filteredChats.map(chat => {
            const title = chat.visitor_name || chat.customer_name || chat.company_name || 'Unknown'
            const resolution = resolutionOf(chat)
            return (
            <div
              key={chat.id}
              onClick={() => navigate(`/queue/${chat.id}`)}
              className={`flex items-center gap-3 px-6 py-3 border-b border-border hover:bg-surface-hover transition-colors cursor-pointer ${
                sel.isSelected(chat.id) ? 'bg-brand-50/60 dark:bg-brand-900/20' : ''
              }`}
            >
              <SelectCheckbox checked={sel.isSelected(chat.id)} onChange={() => sel.toggle(chat.id)} />

              {/* company logo + country flag */}
              <div className="relative shrink-0">
                {chat.company_logo_url ? (
                  <img src={chat.company_logo_url} alt="" className="w-9 h-9 rounded-full object-cover bg-surface-secondary border border-border" />
                ) : (
                  <div className="flex items-center justify-center w-9 h-9 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 text-sm font-semibold">
                    {safeInitial(chat.company_name || title)}
                  </div>
                )}
                {countryToFlag(chat.company_country) && (
                  <span className="absolute -bottom-1 -right-1 text-xs leading-none" title={chat.company_country || ''}>{countryToFlag(chat.company_country)}</span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-txt-primary truncate">{safeString(title, 'Unknown')}</span>
                  <Badge variant="status" value={chat.status} />
                  {chat.status === 'closed' && (
                    <span className={`text-[10px] font-medium px-1.5 py-px rounded ${resolution === 'Resolved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{resolution}</span>
                  )}
                </div>
                <p className="text-xs text-txt-tertiary mt-0.5 truncate">
                  {chat.company_name || 'Unknown company'}
                  {chat.agent_name ? <> · closed by <span className="text-txt-secondary">{chat.agent_name}</span></> : ''}
                  {chat.resolution_summary ? ` · ${chat.resolution_summary}` : ''}
                </p>
              </div>

              {/* meta: closed-at, duration, chat # */}
              <div className="hidden sm:flex flex-col items-end text-[11px] text-txt-tertiary shrink-0 gap-0.5">
                <span title="Closed at">{chat.closed_at ? fmtDateTime(chat.closed_at) : fmtDateTime(chat.created_at)}</span>
                <span className="flex items-center gap-2">
                  <span title="Duration">⏱ {fmtDuration(chat.created_at, chat.closed_at)}</span>
                  <span title="Chat ID" className="font-mono">#{chat.id}</span>
                </span>
              </div>
            </div>
            )
          })
        )}
      </div>

      <AssignAgentModal
        open={assignOpen}
        count={sel.count}
        noun="chat"
        busy={bulkBusy}
        onCancel={() => setAssignOpen(false)}
        onConfirm={handleBulkAssign}
      />
    </div>
  )
}
